// 
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    collection,
    addDoc,
    getDocs,
    doc,
    updateDoc,
    deleteDoc,
    serverTimestamp,
    onSnapshot,
    query,
    where,
    documentId,
    getDoc,
    writeBatch,
} from 'firebase/firestore';
import { BookText, ListTodo, Loader2, Edit, Trash2, AlertTriangle, CheckCircle, Printer, BookOpen, Filter, Edit3, Check, X, Globe, Languages, BookUser } from 'lucide-react';

// ===============================================
// Fonctions utilitaires
// ===============================================

const FlashMessage = ({ type, text }) => (
    <div className={`p-3 mb-4 rounded-lg flex items-center shadow-md transition-all duration-300 ${
        type === 'error'
        ? 'bg-red-100 border border-red-400 text-red-700'
        : 'bg-green-100 border border-green-400 text-green-700'
    }`}>
        {type === 'error' ? <AlertTriangle size={20} className="mr-2" /> : <CheckCircle size={20} className="mr-2" />}
        <span>{text}</span>
    </div>
);

const cleanAndParse = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
};

// Fonction pour déterminer le type de matière à partir du nom
const determineMatiereTypeFromName = (matiereName) => {
    if (!matiereName) return 'français';
    
    const nameLower = matiereName.toLowerCase();
    
    // Liste des matières arabes
    const arabicSubjects = [
        'arabe', 'coran', 'islamique', 'islam', 'fiqh', 'aqida', 
        'tawhid', 'hadith', 'sira', 'théologie', 'religion',
        'quran', 'dini', 'islamiyyah', 'تعليم إسلامي', 'ديني',
        'قرآن', 'تجويد', 'فقه', 'عقيدة', 'حديث', 'سيرة'
    ];
    
    // Liste des matières communes (français + arabe)
    const commonSubjects = [
        'mathématiques', 'maths', 'sciences', 'physique', 'chimie', 
        'histoire', 'géographie', 'informatique', 'sport', 'éducation physique',
        'arts', 'dessin', 'musique', 'biologie', 'géologie', 'anglais', 'espagnol'
    ];
    
    if (arabicSubjects.some(subject => nameLower.includes(subject))) {
        return 'arabe';
    }
    
    if (commonSubjects.some(subject => nameLower.includes(subject))) {
        return 'commun';
    }
    
    return 'français';
};

// ===============================================
// Composant Principal NotesManagement
// ===============================================

const NotesManagement = ({ db, setError, setMessage, schoolId, userRole, currentUserId, error, message, schoolInfo = {} }) => {
    const [activeTab, setActiveTab] = useState('entry');
    
    // --- États pour la saisie des notes ---
    const [eleves, setEleves] = useState([]);
    const [notesParEleve, setNotesParEleve] = useState({});
    const [matiereId, setMatiereId] = useState('');
    const [classeId, setClasseId] = useState('');
    const [trimestre, setTrimestre] = useState('');
    const [anneeScolaire, setAnneeScolaire] = useState('');
    const [typeEvaluation, setTypeEvaluation] = useState('');
    
        
    // --- États pour la gestion des données ---
    const [matieresGlobales, setMatieresGlobales] = useState([]);
    const [classes, setClasses] = useState([]);
    const [filteredClasses, setFilteredClasses] = useState([]);
    const [matieresAttribuees, setMatieresAttribuees] = useState([]);
    const [matieresDisponibles, setMatieresDisponibles] = useState([]);
    
    // États pour les liaisons classe-matières
    const [liaisonsClasseMatieres, setLiaisonsClasseMatieres] = useState([]);
    
    const [niveau, setNiveau] = useState('');
    const [availableNiveaux, setAvailableNiveaux] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingEleves, setLoadingEleves] = useState(false);
    const [loadingMatieres, setLoadingMatieres] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    // --- États pour l'affichage des notes enregistrées (Filtrage) ---
    const [filteredNotes, setFilteredNotes] = useState([]);
    const [loadingFilteredNotes, setLoadingFilteredNotes] = useState(false);
    const [filterClasseId, setFilterClasseId] = useState('');
    const [filterStudentName, setFilterStudentName] = useState('');
    const [filterTrimestre, setFilterTrimestre] = useState('');
    const [filterAnneeScolaire, setFilterAnneeScolaire] = useState('');
    const [filterTypeEvaluation, setFilterTypeEvaluation] = useState('');
    
    // --- États pour la Vue Globale ---
    const [aggregatedNotes, setAggregatedNotes] = useState([]);
    const [loadingAggregatedNotes, setLoadingAggregatedNotes] = useState(false);
    
    // --- États pour l'édition ---
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [originalEleveIdForEdit, setOriginalEleveIdForEdit] = useState(null);
    const [schoolData, setSchoolData] = useState(null);
    
    // --- États pour l'édition en masse ---
    const [isMassEditMode, setIsMassEditMode] = useState(false);
    const [massEditMatiereId, setMassEditMatiereId] = useState('');
    const [massEditValues, setMassEditValues] = useState({});
    const [massEditLoading, setMassEditLoading] = useState(false);
    const [selectedNotesForMassEdit, setSelectedNotesForMassEdit] = useState([]);
    const [selectAllMassEdit, setSelectAllMassEdit] = useState(false);
    
    // --- NOUVEAU : États pour charger les notes existantes pour l'édition en masse ---
    const [existingNotesForMassEdit, setExistingNotesForMassEdit] = useState({});
    const [loadingExistingNotes, setLoadingExistingNotes] = useState(false);
    
    // --- Détection du niveau primaire/préscolaire ---
    const [isPrimaire, setIsPrimaire] = useState(false);
    
    // --- NOUVEAU : États pour l'intégration franco-arabe ---
    const [isFrancoArabeSchool, setIsFrancoArabeSchool] = useState(false);
    const [selectedMatiereTypeFilter, setSelectedMatiereTypeFilter] = useState('tous'); // 'tous', 'français', 'arabe'

    // --- NOUVEAU : États pour les permissions enseignant ---
    const [teacherAssignedClasses, setTeacherAssignedClasses] = useState([]);
    const [loadingTeacherPermissions, setLoadingTeacherPermissions] = useState(false);
    const [teacherId, setTeacherId] = useState(null);

    // --- NOUVEAU : États pour le cache des données ---
    const teacherDataCache = useRef({
    lastFetch: null,
    data: null
});

    // Fonction pour générer les années scolaires
    const generateAcademicYears = useCallback(() => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = 0; i < 5; i++) {
            const year = currentYear - i;
            years.push(`${year}-${year + 1}`);
        }
        return years;
    }, []);
    
    // Options pour le type d'évaluation
    const typeEvaluationOptions = useMemo(() => [
        { value: 'Devoir', label: 'Devoir' },
        { value: 'Composition', label: 'Composition' },
        { value: 'Oral', label: 'Oral' },
        { value: 'Évaluation', label: 'Évaluation (Primaire)' },
    ], []);

    // --- Fonctions utilitaires ---
    const displayMessage = useCallback(
        (msg, isError = false) => {
            let timer;
            if (isError) {
                setError(msg);
                timer = setTimeout(() => setError(null), 4000);
            } else {
                setMessage(msg);
                timer = setTimeout(() => setMessage(null), 3000);
            }
            return () => clearTimeout(timer);
        },
        [setError, setMessage]
    );

    // --- Détection du niveau primaire/préscolaire ---
    useEffect(() => {
        const niveauLower = niveau.toLowerCase();
        const isPrimaireLevel = niveauLower.includes('élémentaire') || 
                                niveauLower.includes('préscolaire') || 
                                niveauLower.includes('prescolaire') ||
                                niveauLower.includes('primaire') ||
                                niveauLower.includes('maternelle');
        
        setIsPrimaire(isPrimaireLevel);
        
        // Si c'est primaire, forcer le type d'évaluation à "Évaluation"
        if (isPrimaireLevel && !editingNoteId) {
            setTypeEvaluation('Évaluation');
        }
    }, [niveau, editingNoteId]);

    // --- Charger les données de l'école (une seule fois) ---
    useEffect(() => {
        if (!db || !schoolId) {
            setSchoolData(null);
            return;
        }

        const fetchSchoolData = async () => {
            try {
                const schoolDocRef = doc(db, 'schools', schoolId);
                const schoolSnap = await getDoc(schoolDocRef);
                
                if (schoolSnap.exists()) {
                    const data = schoolSnap.data();
                    setSchoolData(data);
                    setIsFrancoArabeSchool(data.isFrancoArabe || false);
                } else {
                    setSchoolData(null);
                    console.warn("Le document de l'école n'existe pas:", schoolId);
                }
            } catch (error) {
                console.error("Erreur lors du chargement des données de l'école:", error);
                setError("Erreur lors du chargement des données de l'école.");
            }
        };

        fetchSchoolData();
    }, [db, schoolId, setError]);

    // --- NOUVEAU : Charger les classes affectées à l'enseignant (optimisé avec cache) ---
    // --- NOUVEAU : Charger les classes affectées à l'enseignant (optimisé avec useRef) ---
    useEffect(() => {
        const fetchTeacherAssignedClasses = async () => {
            if (!db || !schoolId || !currentUserId || userRole !== 'enseignant') {
                setTeacherAssignedClasses([]);
                setLoadingTeacherPermissions(false);
                return;
            }

            // Vérifier le cache (5 minutes de cache) via .current
            const now = Date.now();
            if (teacherDataCache.current.lastFetch && 
                teacherDataCache.current.data && 
                (now - teacherDataCache.current.lastFetch < 5 * 60 * 1000)) {
                
                // Utilisation des données du cache sans déclencher de re-rendu inutile
                setTeacherAssignedClasses(teacherDataCache.current.data.assignedClasses);
                setTeacherId(teacherDataCache.current.data.teacherId);
                setLoadingTeacherPermissions(false);
                return;
            }
            
            setLoadingTeacherPermissions(true);
            try {
                // Récupérer l'enseignant correspondant à l'utilisateur connecté
                const enseignantsRef = collection(db, 'schools', schoolId, 'enseignants');
                const q = query(enseignantsRef, where('userId', '==', currentUserId));
                const enseignantSnapshot = await getDocs(q);
                
                if (!enseignantSnapshot.empty) {
                    const enseignantDoc = enseignantSnapshot.docs[0];
                    const enseignantId = enseignantDoc.id;
                    setTeacherId(enseignantId);
                    
                    // Récupérer les affectations de classes de cet enseignant
                    const assignmentsRef = collection(db, 'schools', schoolId, 'teacherClassAssignments');
                    const assignmentsQuery = query(
                        assignmentsRef, 
                        where('teacherId', '==', enseignantId)
                    );
                    const assignmentsSnapshot = await getDocs(assignmentsQuery);
                    const assignedClasses = assignmentsSnapshot.docs.map(doc => doc.data().classId);
                    
                    // Mettre en cache les données dans la REF (pas de setState ici)
                    teacherDataCache.current = {
                        lastFetch: now,
                        data: {
                            teacherId: enseignantId,
                            assignedClasses: assignedClasses,
                            timestamp: now
                        }
                    };

                    setTeacherAssignedClasses(assignedClasses);
                    
                    // Afficher un message si aucune classe n'est affectée
                    if (assignedClasses.length === 0) {
                        setMessage("Vous n'êtes actuellement affecté à aucune classe. Contactez l'administration.");
                    } else {
                        // Optionnel : ne pas spammer le message à chaque rechargement
                        // setMessage(`Vous êtes affecté à ${assignedClasses.length} classe(s).`);
                    }
                } else {
                    setMessage("Vous n'êtes pas enregistré comme enseignant dans cette école. Contactez l'administration.");
                    setTeacherAssignedClasses([]);
                }
            } catch (error) {
                console.error("Erreur lors du chargement des classes de l'enseignant:", error);
                setError("Erreur lors du chargement de vos classes affectées.");
                setTeacherAssignedClasses([]);
            } finally {
                setLoadingTeacherPermissions(false);
            }
        };
        
        fetchTeacherAssignedClasses();
    // Notez que teacherDataCache n'est PLUS dans les dépendances
    }, [db, schoolId, currentUserId, userRole, setMessage, setError]);

    // --- Fonction pour vérifier si l'enseignant peut accéder à une classe ---
    // --- Fonction pour vérifier si l'enseignant peut accéder à une classe ---
    const canAccessClass = useCallback((classId) => {
        if (userRole !== 'enseignant') return true;
        if (!classId || teacherAssignedClasses.length === 0) return false;
        return teacherAssignedClasses.includes(classId);
    }, [userRole, teacherAssignedClasses]);

    // --- Fonction pour filtrer les classes selon le rôle ---
    // --- Fonction pour filtrer les classes selon le rôle ---
const updateFilteredClasses = useCallback(() => {
    if (userRole === 'enseignant') {
        // Pour les enseignants, filtrer par classes affectées
        const teacherClasses = classes.filter(classe => teacherAssignedClasses.includes(classe.id));
        setFilteredClasses(teacherClasses);
        
        // Si l'enseignant n'a qu'une seule classe, la sélectionner automatiquement
        if (teacherClasses.length === 1 && !classeId) {
            setClasseId(teacherClasses[0].id);
            setNiveau(teacherClasses[0].niveau || '');
        }
    } else {
        // Pour les autres rôles, filtrer par niveau si sélectionné
        if (niveau) {
            const classesForNiveau = classes.filter(c => c.niveau === niveau);
            setFilteredClasses(classesForNiveau);
            if (classeId && !classesForNiveau.some(c => c.id === classeId)) {
                setClasseId('');
                setMatiereId('');
                setMatieresAttribuees([]);
                setMatieresDisponibles([]);
            }
        } else {
            setFilteredClasses(classes);
        }
    }
}, [userRole, classes, teacherAssignedClasses, niveau, classeId]); // Les dépendances sont correctes

    // --- Mettre à jour filteredClasses quand les données changent ---
    useEffect(() => {
       updateFilteredClasses();
}, [classes, teacherAssignedClasses, niveau, classeId, userRole]);
    // --- Chargement des niveaux disponibles (une seule fois) ---
    useEffect(() => {
        if (!db || !schoolId) {
            setAvailableNiveaux([]);
            return;
        }

        const classesCollectionRef = collection(db, 'schools', schoolId, 'classes');
        const unsubscribe = onSnapshot(query(classesCollectionRef), (snapshot) => {
            const niveaux = new Set();
            snapshot.docs.forEach((doc) => {
                const data = doc.data();
                if (data.niveau) niveaux.add(data.niveau);
            });
            setAvailableNiveaux(Array.from(niveaux).sort());
        });

        return () => unsubscribe();
    }, [db, schoolId]);

    // --- Chargement des Classes (subscription optimisée) ---
    useEffect(() => {
        if (!db || !schoolId) return;

        const unsubClasses = onSnapshot(
            collection(db, 'schools', schoolId, 'classes'),
            (snapshot) => {
                const classesData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
                setClasses(classesData);
            },
            (error) => {
                console.error('Erreur lors du chargement des classes:', error);
                setError('Erreur lors du chargement des classes.');
            }
        );
        
        return () => unsubClasses();
    }, [db, schoolId, setError]);

    // --- Chargement des Matières Globales (subscription optimisée) ---
    useEffect(() => {
        if (!db || !schoolId) return;

        setLoadingMatieres(true);

        const q = query(
            collection(db, 'schools', schoolId, 'matieres_globales'),
            where('name', '!=', '')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const matieresData = snapshot.docs.map(doc => ({ 
                id: doc.id, 
                ...doc.data() 
            }));
            setMatieresGlobales(matieresData);
            setLoadingMatieres(false);
        }, (error) => {
            console.error("Erreur chargement matières globales:", error);
            setError("Erreur lors du chargement des matières.");
            setLoadingMatieres(false);
        });

        return () => unsubscribe();
    }, [db, schoolId, setError]);

    // --- Chargement des liaisons classe-matières (subscription optimisée) ---
    useEffect(() => {
        if (!db || !schoolId) return;

        const q = query(
            collection(db, 'schools', schoolId, 'class_matieres')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const liaisonsData = snapshot.docs.map(doc => ({ 
                id: doc.id, 
                ...doc.data() 
            }));
            setLiaisonsClasseMatieres(liaisonsData);
        }, (error) => {
            console.error("Erreur chargement liaisons classe-matières:", error);
            setError("Erreur lors du chargement des attributions de matières.");
        });

        return () => unsubscribe();
    }, [db, schoolId, setError]);

    // --- NOUVEAU : Filtrer les matières disponibles selon le type ---
    const getFilteredMatieresForEntry = useCallback(() => {
        let filtered = matieresAttribuees;
        
        if (isFrancoArabeSchool && selectedMatiereTypeFilter !== 'tous') {
            filtered = matieresAttribuees.filter(matiere => {
                const type = matiere.matiereType || matiere.type || 'français';
                if (selectedMatiereTypeFilter === 'français') {
                    return type === 'français' || type === 'commun';
                } else if (selectedMatiereTypeFilter === 'arabe') {
                    return type === 'arabe' || type === 'commun';
                }
                return true;
            });
        }
        
        // En mode édition, inclure la matière en cours d'édition même si elle n'est plus attribuée
        if (editingNoteId && matiereId && !filtered.some(m => m.id === matiereId)) {
            const matiereEnEdition = matieresGlobales.find(m => m.id === matiereId);
            if (matiereEnEdition) {
                return [matiereEnEdition, ...filtered];
            }
        }
        
        return filtered;
    }, [matieresAttribuees, isFrancoArabeSchool, selectedMatiereTypeFilter, editingNoteId, matiereId, matieresGlobales]);

    // --- Trouver les matières attribuées à la classe sélectionnée ---
    useEffect(() => {
        if (!classeId || !matieresGlobales.length || !liaisonsClasseMatieres.length) {
            setMatieresAttribuees([]);
            setMatieresDisponibles([]);
            setMatiereId('');
            return;
        }

        // Vérifier que l'enseignant a accès à cette classe
        if (userRole === 'enseignant' && !canAccessClass(classeId)) {
            setError("Vous n'êtes pas autorisé à accéder à cette classe.");
            setMatieresAttribuees([]);
            setMatieresDisponibles([]);
            setMatiereId('');
            return;
        }

        const liaisonsDeLaClasse = liaisonsClasseMatieres.filter(
            liaison => liaison.classId === classeId
        );

        const matieresAttribueesData = liaisonsDeLaClasse
            .map(liaison => {
                const matiere = matieresGlobales.find(m => m.id === liaison.matiereId);
                if (matiere) {
                    // Déterminer le type de matière : priorité à la liaison, puis à la matière globale
                    const matiereType = liaison.matiereType || 
                                       matiere.type || 
                                       determineMatiereTypeFromName(matiere.name);
                    
                    return {
                        ...matiere,
                        liaisonId: liaison.id,
                        coefficient: liaison.coefficient || 1,
                        noteSur: liaison.noteSur || (isPrimaire ? 10 : 20),
                        serie: liaison.serie || '',
                        matiereType: matiereType,
                        // Conserver aussi le type original pour référence
                        type: matiere.type || 'français'
                    };
                }
                return null;
            })
            .filter(Boolean);

        setMatieresAttribuees(matieresAttribueesData);
        
        // Mettre à jour les matières disponibles (filtrées)
        const filteredMatieres = getFilteredMatieresForEntry();
        setMatieresDisponibles(filteredMatieres);

        if (matiereId && !filteredMatieres.some(m => m.id === matiereId) && !editingNoteId) {
            setMatiereId('');
        }

    }, [classeId, matieresGlobales, liaisonsClasseMatieres, editingNoteId, matiereId, isPrimaire, userRole, canAccessClass, setError, getFilteredMatieresForEntry, isFrancoArabeSchool, selectedMatiereTypeFilter]);

    // --- NOUVEAU : Charger les notes existantes pour l'édition en masse ---
    useEffect(() => {
        const loadExistingNotesForMassEdit = async () => {
            if (!isMassEditMode || !massEditMatiereId || !classeId || !trimestre || !anneeScolaire || !typeEvaluation || !db || !schoolId) {
                setExistingNotesForMassEdit({});
                return;
            }

            setLoadingExistingNotes(true);
            try {
                const q = query(
                    collection(db, 'schools', schoolId, 'notes'),
                    where('classeId', '==', classeId),
                    where('matiereId', '==', massEditMatiereId),
                    where('trimestre', '==', trimestre),
                    where('anneeScolaire', '==', anneeScolaire),
                    where('typeEvaluation', '==', typeEvaluation)
                );

                const querySnapshot = await getDocs(q);
                const existingNotes = {};

                querySnapshot.forEach(doc => {
                    const noteData = doc.data();
                    existingNotes[noteData.eleveId] = {
                        valeurNote: noteData.valeurNote,
                        valeurNoteDevoir1: noteData.valeurNoteDevoir1,
                        valeurNoteDevoir2: noteData.valeurNoteDevoir2,
                        matiereType: noteData.matiereType
                    };
                });

                setExistingNotesForMassEdit(existingNotes);
                
                // Pré-remplir les valeurs d'édition en masse avec les notes existantes
                const initialMassEditValues = {};
                eleves.forEach(eleve => {
                    const existingNote = existingNotes[eleve.id];
                    if (existingNote) {
                        if (typeEvaluation === 'Devoir') {
                            initialMassEditValues[eleve.id] = {
                                valeurNoteDevoir1: existingNote.valeurNoteDevoir1 !== null && existingNote.valeurNoteDevoir1 !== undefined ? existingNote.valeurNoteDevoir1 : '',
                                valeurNoteDevoir2: existingNote.valeurNoteDevoir2 !== null && existingNote.valeurNoteDevoir2 !== undefined ? existingNote.valeurNoteDevoir2 : '',
                                valeurNote: ''
                            };
                        } else {
                            initialMassEditValues[eleve.id] = {
                                valeurNoteDevoir1: '',
                                valeurNoteDevoir2: '',
                                valeurNote: existingNote.valeurNote !== null && existingNote.valeurNote !== undefined ? existingNote.valeurNote : ''
                            };
                        }
                    } else {
                        initialMassEditValues[eleve.id] = {
                            valeurNoteDevoir1: '',
                            valeurNoteDevoir2: '',
                            valeurNote: ''
                        };
                    }
                });

                setMassEditValues(initialMassEditValues);
                
            } catch (error) {
                console.error("Erreur lors du chargement des notes existantes pour l'édition en masse:", error);
                setError("Erreur lors du chargement des notes existantes.");
            } finally {
                setLoadingExistingNotes(false);
            }
        };

        loadExistingNotesForMassEdit();
    }, [isMassEditMode, massEditMatiereId, classeId, trimestre, anneeScolaire, typeEvaluation, db, schoolId, eleves, setError]);

    // --- Charger les élèves via les inscriptions (optimisé avec annulation) ---
    useEffect(() => {
        if (!classeId || !db || !schoolId) {
            setEleves([]);
            setNotesParEleve({});
            return;
        }

        // Vérifier que l'enseignant a accès à cette classe
        if (userRole === 'enseignant' && !canAccessClass(classeId)) {
            setError("Vous n'êtes pas autorisé à accéder aux élèves de cette classe.");
            setEleves([]);
            setNotesParEleve({});
            return;
        }

        setLoadingEleves(true);

        const enrollmentsRef = collection(db, 'schools', schoolId, 'enrollments');
        const qEnrollments = query(enrollmentsRef, where('classId', '==', classeId));

        let isMounted = true;

        const unsubscribe = onSnapshot(qEnrollments, async (enrollmentSnapshot) => {
            if (!isMounted) return;

            const studentIds = enrollmentSnapshot.docs.map(doc => doc.data().studentId);

            if (studentIds.length === 0) {
                if (isMounted) {
                    setEleves([]);
                    setLoadingEleves(false);
                }
                return;
            }

            const studentsData = [];
            
            try {
                // Utiliser une seule requête avec 'in' (limité à 10, mais c'est OK pour une classe)
                if (studentIds.length <= 10) {
                    const studentsQuery = query(
                        collection(db, 'schools', schoolId, 'eleves'),
                        where(documentId(), 'in', studentIds)
                    );
                    
                    const studentsSnapshot = await getDocs(studentsQuery);
                    studentsSnapshot.forEach(doc => {
                        const data = doc.data();
                        const nomComplet = data.name || (data.prenom && data.nom ? `${data.prenom} ${data.nom}` : 'N/A');
                        studentsData.push({ id: doc.id, name: nomComplet, ...data });
                    });
                } else {
                    // Pour plus de 10 élèves, faire des requêtes par lot de 10
                    const BATCH_SIZE = 10;
                    for (let i = 0; i < studentIds.length; i += BATCH_SIZE) {
                        const batchIds = studentIds.slice(i, i + BATCH_SIZE);
                        const studentsQuery = query(
                            collection(db, 'schools', schoolId, 'eleves'),
                            where(documentId(), 'in', batchIds)
                        );
                        
                        const studentsSnapshot = await getDocs(studentsQuery);
                        studentsSnapshot.forEach(doc => {
                            const data = doc.data();
                            const nomComplet = data.name || (data.prenom && data.nom ? `${data.prenom} ${data.nom}` : 'N/A');
                            studentsData.push({ id: doc.id, name: nomComplet, ...data });
                        });
                    }
                }

                if (isMounted) {
                    setEleves(studentsData.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
                    setNotesParEleve({});
                    setLoadingEleves(false);
                }
            } catch (fetchError) {
                console.error("Erreur lors de la récupération des détails des élèves:", fetchError);
                if (isMounted) {
                    setError("Erreur lors de la récupération des détails des élèves.");
                    setLoadingEleves(false);
                }
            }
        }, (error) => {
            console.error("Erreur lors du chargement des inscriptions:", error);
            if (isMounted) {
                setError("Erreur lors du chargement des élèves inscrits.");
                setLoadingEleves(false);
            }
        });

        return () => {
            isMounted = false;
            unsubscribe();
        };
    }, [db, schoolId, classeId, setError, userRole, canAccessClass]);

    // --- handleNoteChange pour gérer les deux devoirs OU une seule note pour primaire ---
    const handleNoteChange = useCallback((eleveId, note, fieldName = 'valeurNote') => {
        setNotesParEleve((prev) => ({ 
            ...prev, 
            [eleveId]: { 
                ...(prev[eleveId] || {}), 
                [fieldName]: note 
            }
        }));
        setHasChanges(true);
    }, []);

    // --- NOUVEAU : Fonction pour l'édition en masse ---
    const handleStartMassEdit = useCallback((matiereId) => {
        if (!matiereId || !classeId || !trimestre || !anneeScolaire) {
            setError('Veuillez d\'abord sélectionner une classe, une matière, un trimestre et une année scolaire.');
            return;
        }

        // Vérifier que l'enseignant a accès à cette classe
        if (userRole === 'enseignant' && !canAccessClass(classeId)) {
            setError("Vous n'êtes pas autorisé à éditer les notes de cette classe.");
            return;
        }

        setIsMassEditMode(true);
        setMassEditMatiereId(matiereId);
        setSelectedNotesForMassEdit([]);
        setSelectAllMassEdit(false);
        
        // Les notes existantes seront chargées automatiquement via le useEffect
    }, [matiereId, classeId, trimestre, anneeScolaire, userRole, canAccessClass, setError]);

    const handleCancelMassEdit = useCallback(() => {
        setIsMassEditMode(false);
        setMassEditMatiereId('');
        setMassEditValues({});
        setSelectedNotesForMassEdit([]);
        setSelectAllMassEdit(false);
        setExistingNotesForMassEdit({});
    }, []);

    const handleMassEditChange = useCallback((eleveId, value, fieldName) => {
        setMassEditValues(prev => ({
            ...prev,
            [eleveId]: {
                ...(prev[eleveId] || {}),
                [fieldName]: value
            }
        }));
    }, []);

    const handleToggleNoteForMassEdit = useCallback((eleveId) => {
        setSelectedNotesForMassEdit(prev => {
            if (prev.includes(eleveId)) {
                return prev.filter(id => id !== eleveId);
            } else {
                return [...prev, eleveId];
            }
        });
    }, []);

    const handleSelectAllMassEdit = useCallback(() => {
        if (selectAllMassEdit) {
            setSelectedNotesForMassEdit([]);
        } else {
            setSelectedNotesForMassEdit(eleves.map(eleve => eleve.id));
        }
        setSelectAllMassEdit(!selectAllMassEdit);
    }, [selectAllMassEdit, eleves]);

    const handleApplyMassEdit = useCallback(async () => {
        if (selectedNotesForMassEdit.length === 0) {
            setError('Veuillez sélectionner au moins un élève à modifier.');
            return;
        }

        // Vérifier que l'enseignant a accès à cette classe
        if (userRole === 'enseignant' && !canAccessClass(classeId)) {
            setError("Vous n'êtes pas autorisé à modifier les notes de cette classe.");
            return;
        }

        setMassEditLoading(true);
        setError(null);
        setMessage(null);

        try {
            const batch = writeBatch(db);
            let notesUpdatedCount = 0;
            let notesCreatedCount = 0;

            const matiereInfo = matieresGlobales.find(m => m.id === massEditMatiereId);
            const matiereNom = matiereInfo?.name || massEditMatiereId;
            const classeNom = classes.find(c => c.id === classeId)?.name || classes.find(c => c.id === classeId)?.nom;

            // Récupérer la note maximale pour cette matière dans cette classe
            const liaison = liaisonsClasseMatieres.find(
                l => l.classId === classeId && l.matiereId === massEditMatiereId
            );
            const noteMax = liaison?.noteSur || (isPrimaire ? 10 : 20);
            
            // DÉTERMINER LE TYPE DE MATIÈRE
            const matiereType = liaison?.matiereType || 
                               matiereInfo?.type || 
                               determineMatiereTypeFromName(matiereNom) || 
                               'français';

            for (const eleveId of selectedNotesForMassEdit) {
                const eleve = eleves.find(e => e.id === eleveId);
                if (!eleve) continue;

                const eleveNom = eleve.name || eleve.nom;
                const studentAuthIdValue = eleve.uid || eleve.userId || eleve.authId || null;
                const massEditData = massEditValues[eleveId] || {};

                let noteData = {
                    anneeScolaire: anneeScolaire,
                    classeId: classeId,
                    niveau: niveau,
                    classeNom: classeNom,
                    eleveId: eleveId,
                    eleveNom: eleveNom,
                    matiereId: massEditMatiereId,
                    matiereNom: matiereNom,
                    matiereType: matiereType,
                    trimestre: trimestre,
                    typeEvaluation: typeEvaluation,
                    updatedAt: serverTimestamp(),
                    studentAuthUID: studentAuthIdValue,
                    updatedByUserId: currentUserId || null,
                };

                let isValid = false;
                let hasValue = false;

                if (typeEvaluation === 'Devoir') {
                    const note1 = cleanAndParse(massEditData.valeurNoteDevoir1);
                    const note2 = cleanAndParse(massEditData.valeurNoteDevoir2);

                    // Vérifier si au moins une note est fournie
                    if (note1 !== null || note2 !== null) {
                        noteData.valeurNoteDevoir1 = note1;
                        noteData.valeurNoteDevoir2 = note2;
                        noteData.valeurNote = null;
                        isValid = true;
                        hasValue = true;
                    }
                    
                    // Validation des valeurs
                    if ((note1 !== null && (isNaN(note1) || note1 < 0 || note1 > noteMax)) || 
                        (note2 !== null && (isNaN(note2) || note2 < 0 || note2 > noteMax))) {
                        setError(`Note de Devoir invalide pour l'élève ${eleveNom}. La note maximale est ${noteMax}.`);
                        setMassEditLoading(false);
                        return;
                    }
                } else {
                    const valeurNote = cleanAndParse(massEditData.valeurNote);
                    if (valeurNote !== null) {
                        noteData.valeurNote = valeurNote;
                        noteData.valeurNoteDevoir1 = null;
                        noteData.valeurNoteDevoir2 = null;
                        isValid = true;
                        hasValue = true;
                    }
                    
                    // Validation
                    if (valeurNote !== null && (isNaN(valeurNote) || valeurNote < 0 || valeurNote > noteMax)) {
                        setError(`Note invalide pour l'élève ${eleveNom}. La note maximale est ${noteMax}.`);
                        setMassEditLoading(false);
                        return;
                    }
                }

                // Si pas de valeur, on supprime la note existante si elle existe
                if (!hasValue) {
                    // Chercher si une note existe
                    const q = query(
                        collection(db, 'schools', schoolId, 'notes'),
                        where('eleveId', '==', eleveId),
                        where('matiereId', '==', massEditMatiereId),
                        where('trimestre', '==', trimestre),
                        where('anneeScolaire', '==', anneeScolaire),
                        where('typeEvaluation', '==', typeEvaluation)
                    );

                    const existingNotesSnapshot = await getDocs(q);
                    if (!existingNotesSnapshot.empty) {
                        const existingDoc = existingNotesSnapshot.docs[0];
                        await deleteDoc(doc(db, 'schools', schoolId, 'notes', existingDoc.id));
                        notesUpdatedCount++;
                    }
                    continue;
                }

                if (!isValid) continue;

                // Chercher si une note existe déjà
                const q = query(
                    collection(db, 'schools', schoolId, 'notes'),
                    where('eleveId', '==', eleveId),
                    where('matiereId', '==', massEditMatiereId),
                    where('trimestre', '==', trimestre),
                    where('anneeScolaire', '==', anneeScolaire),
                    where('typeEvaluation', '==', typeEvaluation)
                );

                const existingNotesSnapshot = await getDocs(q);

                if (!existingNotesSnapshot.empty) {
                    const existingDoc = existingNotesSnapshot.docs[0];
                    const noteDocRef = doc(db, 'schools', schoolId, 'notes', existingDoc.id);

                    let updatePayload = {
                        ...noteData,
                        matiereType: matiereType,
                        createdAt: existingDoc.data().createdAt
                    };

                    batch.update(noteDocRef, updatePayload);
                    notesUpdatedCount++;
                } else {
                    noteData.createdAt = serverTimestamp();
                    noteData.createdByUserId = currentUserId || null;
                    const newNoteRef = doc(collection(db, 'schools', schoolId, 'notes'));
                    batch.set(newNoteRef, noteData);
                    notesCreatedCount++;
                }
            }

            if (notesUpdatedCount > 0 || notesCreatedCount > 0) {
                await batch.commit();
                setMessage(`Édition en masse réussie : ${notesCreatedCount} nouvelles notes créées, ${notesUpdatedCount} notes mises à jour.`);
                
                handleCancelMassEdit();
            } else {
                setMessage('Aucune modification appliquée.');
            }

        } catch (error) {
            console.error("Erreur lors de l'édition en masse:", error);
            setError("Erreur lors de l'édition en masse : " + error.message);
        } finally {
            setMassEditLoading(false);
        }
    }, [selectedNotesForMassEdit, userRole, canAccessClass, classeId, db, schoolId, massEditValues, eleves, anneeScolaire, niveau, typeEvaluation, currentUserId, matieresGlobales, massEditMatiereId, classes, liaisonsClasseMatieres, isPrimaire, trimestre, setError, setMessage, handleCancelMassEdit]);

    // --- handleEditNote pour permettre de changer la matière ---
    const handleEditNote = useCallback((note) => {
        // Vérifier que l'enseignant a accès à cette note
        if (userRole === 'enseignant' && !canAccessClass(note.classeId)) {
            setError("Vous n'êtes pas autorisé à modifier cette note.");
            return;
        }

        setEditingNoteId(note.id);
        setOriginalEleveIdForEdit(note.eleveId);
        setActiveTab('entry');
        setHasChanges(true);

        setAnneeScolaire(note.anneeScolaire);
        setTrimestre(note.trimestre);
        setNiveau(note.niveau || '');
        setClasseId(note.classeId);
        setTypeEvaluation(note.typeEvaluation || 'Devoir');
        
        // Permettre de changer la matière
        setMatiereId(note.matiereId);

        let initialNotes = {};
        if (note.typeEvaluation === 'Devoir') {
            initialNotes[note.eleveId] = {
                valeurNoteDevoir1: note.valeurNoteDevoir1 !== undefined && note.valeurNoteDevoir1 !== null ?
                                   note.valeurNoteDevoir1 : 
                                   (note.valeurNote !== undefined && note.valeurNote !== null ? note.valeurNote : ''),
                valeurNoteDevoir2: note.valeurNoteDevoir2 !== undefined && note.valeurNoteDevoir2 !== null ?
                                   note.valeurNoteDevoir2 : '',
            };
        } else {
            initialNotes[note.eleveId] = { 
                valeurNote: note.valeurNote !== undefined && note.valeurNote !== null ?
                           note.valeurNote : '' 
            };
        }
        setNotesParEleve(initialNotes);
        setMessage(`Vous éditez la note de ${note.eleveNom} en ${note.matiereNom} (${note.typeEvaluation || 'Devoir'}).`);
    }, [userRole, canAccessClass, setError, setMessage]);

    // --- handleCancelEdit ---
    const handleCancelEdit = useCallback(() => {
        setEditingNoteId(null);
        setOriginalEleveIdForEdit(null);
        setNotesParEleve({});
        setAnneeScolaire('');
        setTrimestre('');
        setClasseId('');
        setMatiereId('');
        setTypeEvaluation('');
        setNiveau('');
        setMessage(null);
        setError(null);
        setHasChanges(false);
    }, [setMessage, setError]);

    // --- fetchFilteredNotes ---
    const fetchFilteredNotes = useCallback(async () => {
        if (!db || !schoolId || activeTab !== 'saved') {
            setFilteredNotes([]);
            return;
        }

        setLoadingFilteredNotes(true);
        setError(null);

        try {
            let notesQueryRef = collection(db, 'schools', schoolId, 'notes');

            // Pour les enseignants, filtrer par leurs classes
            if (userRole === 'enseignant') {
                if (teacherAssignedClasses.length > 0) {
                    notesQueryRef = query(notesQueryRef, where('classeId', 'in', teacherAssignedClasses));
                } else {
                    // Si l'enseignant n'a aucune classe, retourner un tableau vide
                    setFilteredNotes([]);
                    setLoadingFilteredNotes(false);
                    return;
                }
            }

            if (filterClasseId) {
                // Vérifier que l'enseignant a accès à cette classe
                if (userRole === 'enseignant' && !canAccessClass(filterClasseId)) {
                    setError("Vous n'êtes pas autorisé à voir les notes de cette classe.");
                    setFilteredNotes([]);
                    setLoadingFilteredNotes(false);
                    return;
                }
                notesQueryRef = query(notesQueryRef, where('classeId', '==', filterClasseId));
            }
            if (filterTrimestre) {
                notesQueryRef = query(notesQueryRef, where('trimestre', '==', filterTrimestre));
            }
            if (filterAnneeScolaire) {
                notesQueryRef = query(notesQueryRef, where('anneeScolaire', '==', filterAnneeScolaire));
            }
            if (filterTypeEvaluation) {
                notesQueryRef = query(notesQueryRef, where('typeEvaluation', '==', filterTypeEvaluation));
            }

            const querySnapshot = await getDocs(notesQueryRef);
            let notesData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            notesData = notesData.map(note => {
                const classe = classes.find(c => c.id === note.classeId);
                const note1 = note.valeurNoteDevoir1 !== undefined && note.valeurNoteDevoir1 !== null ? parseFloat(note.valeurNoteDevoir1) : undefined;
                const note2 = note.valeurNoteDevoir2 !== undefined && note.valeurNoteDevoir2 !== null ? parseFloat(note.valeurNoteDevoir2) : undefined;
                
                // Déterminer le type de matière si manquant
                const matiereType = note.matiereType || determineMatiereTypeFromName(note.matiereNom) || 'français';

                return {
                    ...note,
                    classeNom: note.classeNom || classe?.name || classe?.nom || 'N/A', 
                    valeurNoteDevoir1: note1,
                    valeurNoteDevoir2: note2,
                    matiereType: matiereType,
                };
            });
            
            if (filterStudentName) {
                const lowerCaseFilter = filterStudentName.toLowerCase();
                notesData = notesData.filter(note =>
                    note.eleveNom && note.eleveNom.toLowerCase().includes(lowerCaseFilter)
                );
            }

            notesData.sort((a, b) => {
                const classeA = a.classeNom || '';
                const classeB = b.classeNom || '';
                const eleveA = a.eleveNom || '';
                const eleveB = b.eleveNom || '';

                if (classeA !== classeB) {
                    return classeA.localeCompare(classeB);
                }
                return eleveA.localeCompare(eleveB);
            });
            
            setFilteredNotes(notesData);
        } catch (error) {
            console.error("Erreur lors du chargement des notes enregistrées:", error);
            setError("Erreur lors du chargement des notes enregistrées.");
        } finally {
            setLoadingFilteredNotes(false);
        }
    }, [db, schoolId, activeTab, filterClasseId, filterTrimestre, filterAnneeScolaire, filterStudentName, filterTypeEvaluation, setError, classes, userRole, teacherAssignedClasses, canAccessClass]);

    // --- fetchAggregatedNotes ---
    const fetchAggregatedNotes = useCallback(async () => {
        if (!db || !schoolId || activeTab !== 'global') {
            setAggregatedNotes([]);
            return;
        }

        setLoadingAggregatedNotes(true);
        setError(null);

        try {
            let notesQueryRef = collection(db, 'schools', schoolId, 'notes');

            // Pour les enseignants, filtrer par leurs classes
            if (userRole === 'enseignant') {
                if (teacherAssignedClasses.length > 0) {
                    notesQueryRef = query(notesQueryRef, where('classeId', 'in', teacherAssignedClasses));
                } else {
                    // Si l'enseignant n'a aucune classe, retourner un tableau vide
                    setAggregatedNotes([]);
                    setLoadingAggregatedNotes(false);
                    return;
                }
            }

            if (filterClasseId) {
                // Vérifier que l'enseignant a accès à cette classe
                if (userRole === 'enseignant' && !canAccessClass(filterClasseId)) {
                    setError("Vous n'êtes pas autorisé à voir les notes de cette classe.");
                    setAggregatedNotes([]);
                    setLoadingAggregatedNotes(false);
                    return;
                }
                notesQueryRef = query(notesQueryRef, where('classeId', '==', filterClasseId));
            }
            if (filterTrimestre) {
                notesQueryRef = query(notesQueryRef, where('trimestre', '==', filterTrimestre));
            }
            if (filterAnneeScolaire) {
                notesQueryRef = query(notesQueryRef, where('anneeScolaire', '==', filterAnneeScolaire));
            }
            
            const querySnapshot = await getDocs(notesQueryRef);
            let rawNotesData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            if (filterStudentName) {
                const lowerCaseFilter = filterStudentName.toLowerCase();
                rawNotesData = rawNotesData.filter(note =>
                    note.eleveNom && note.eleveNom.toLowerCase().includes(lowerCaseFilter)
                );
            }

            const aggregationMap = new Map();

            for (const note of rawNotesData) {
                const key = `${note.eleveId}-${note.matiereId}-${note.trimestre}-${note.anneeScolaire}`;
                
                if (!aggregationMap.has(key)) {
                    aggregationMap.set(key, {
                        eleveId: note.eleveId,
                        eleveNom: note.eleveNom,
                        classeNom: note.classeNom,
                        matiereNom: note.matiereNom,
                        matiereType: note.matiereType || determineMatiereTypeFromName(note.matiereNom) || 'français',
                        trimestre: note.trimestre,
                        anneeScolaire: note.anneeScolaire,
                        valeurNoteCompoOral: null,
                        valeurNoteDevoir1: null,
                        valeurNoteDevoir2: null,
                    });
                }

                const aggregatedNote = aggregationMap.get(key);

                if (note.typeEvaluation === 'Devoir') {
                    if (note.valeurNoteDevoir1 !== undefined && note.valeurNoteDevoir1 !== null) {
                        aggregatedNote.valeurNoteDevoir1 = parseFloat(note.valeurNoteDevoir1);
                    }
                    if (note.valeurNoteDevoir2 !== undefined && note.valeurNoteDevoir2 !== null) {
                        aggregatedNote.valeurNoteDevoir2 = parseFloat(note.valeurNoteDevoir2);
                    }
                    if (note.valeurNote !== undefined && note.valeurNote !== null && aggregatedNote.valeurNoteDevoir1 === null && aggregatedNote.valeurNoteDevoir2 === null) {
                         aggregatedNote.valeurNoteDevoir1 = parseFloat(note.valeurNote);
                    }
                    
                } else {
                    if (note.valeurNote !== undefined && note.valeurNote !== null) {
                        aggregatedNote.valeurNoteCompoOral = parseFloat(note.valeurNote);
                    }
                }
            }

            const finalNotes = Array.from(aggregationMap.values()).sort((a, b) => {
                const classeA = a.classeNom || '';
                const classeB = b.classeNom || '';
                const eleveA = a.eleveNom || '';
                const eleveB = b.eleveNom || '';
                
                if (classeA !== classeB) {
                    return classeA.localeCompare(classeB);
                }
                return eleveA.localeCompare(eleveB);
            });

            setAggregatedNotes(finalNotes);

        } catch (error) {
            console.error("Erreur lors du chargement des notes agrégées:", error);
            setError("Erreur lors du chargement des notes agrégées.");
        } finally {
            setLoadingAggregatedNotes(false);
        }
    }, [db, schoolId, activeTab, filterClasseId, filterTrimestre, filterAnneeScolaire, filterStudentName, setError, userRole, teacherAssignedClasses, canAccessClass]);

    // --- handleDeleteNote ---
    const handleDeleteNote = useCallback(async (noteId, eleveNom, noteClasseId) => {
        // Vérifier que l'enseignant a accès à cette note
        if (userRole === 'enseignant' && !canAccessClass(noteClasseId)) {
            setError("Vous n'êtes pas autorisé à supprimer cette note.");
            return;
        }

        if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la note de ${eleveNom} ?`)) {
            return;
        }
        setLoading(true);
        try {
            await deleteDoc(doc(db, 'schools', schoolId, 'notes', noteId));
            setMessage('Note supprimée avec succès !');
            
            // Rafraîchir les notes
            if (activeTab === 'saved') {
                await fetchFilteredNotes();
            }
        } catch (error) {
            console.error("Erreur lors de la suppression de la note:", error);
            setError("Erreur lors de la suppression de la note.");
        } finally {
            setLoading(false);
        }
    }, [db, schoolId, userRole, canAccessClass, setError, setMessage, activeTab, fetchFilteredNotes]);

    // --- handlePrintGlobalNotes DÉFINI DANS LE COMPOSANT ---
    const handlePrintGlobalNotes = () => {
        if (aggregatedNotes.length === 0) {
            alert("Aucune note à imprimer.");
            return;
        }

        const schoolName = schoolData?.name || 'Nom de l\'École Inconnu';
        const schoolAddress = schoolData?.adresse_ecole || 'Adresse Inconnue';
        const schoolPhone = schoolData?.telephone_ecole || 'Téléphone Inconnu';
        const schoolLogoUrl = schoolData?.logoUrl || '';

        const tableRows = aggregatedNotes.map(note => `
            <tr>
                <td>${note.classeNom || 'N/A'}</td>
                <td>${note.eleveNom || 'N/A'}</td>
                <td>${note.matiereNom || 'N/A'}</td>
                <td style="text-align: center;">${note.trimestre || 'N/A'}</td>
                <td style="text-align: center;">${note.anneeScolaire || 'N/A'}</td>
                <td style="text-align: center; font-weight: bold;">${note.valeurNoteDevoir1 !== null ? note.valeurNoteDevoir1 : '-'}</td>
                <td style="text-align: center; font-weight: bold;">${note.valeurNoteDevoir2 !== null ? note.valeurNoteDevoir2 : '-'}</td>
                <td style="text-align: center; font-weight: bold;">${note.valeurNoteCompoOral !== null ? note.valeurNoteCompoOral : '-'}</td>
                ${isFrancoArabeSchool ? `<td style="text-align: center;">${note.matiereType || 'français'}</td>` : ''}
            </tr>
        `).join('');

        const printContent = `
            <html>
            <head>
                <title>Synthèse des Notes - ${schoolName}</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 20mm; }
                    .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #ccc; padding-bottom: 10px; }
                    .header img { max-height: 80px; margin-bottom: 10px; }
                    .header h1 { font-size: 20px; margin: 5px 0 2px 0; color: #10B981; }
                    .header p { font-size: 12px; margin: 0; color: #555; }
                    h2 { font-size: 16px; margin-top: 20px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
                    table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 12px; }
                    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                    th { background-color: #f3f4f6; color: #333; font-weight: bold; }
                    tr:nth-child(even) { background-color: #f9f9f9; }
                    .filters { margin-bottom: 20px; font-size: 12px; }
                    .filters span { font-weight: bold; margin-right: 15px; }
                    
                    @media print {
                        body { margin: 0; padding: 0; }
                        .header { border-bottom: 2px solid #333; }
                        table { border: 1px solid #000; }
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    ${schoolLogoUrl ? `<img src="${schoolLogoUrl}" alt="Logo de l'école">` : ''}
                    <h1>${schoolName}</h1>
                    <p>${schoolAddress}</p>
                    <p>${schoolPhone}</p>
                    <p style="margin-top: 15px;">Fait le : ${new Date().toLocaleDateString('fr-FR')}</p>
                </div>

                <h2>Synthèse des Notes Globales (Vue Agrégée)</h2>
                ${isFrancoArabeSchool ? '<p style="color: green; font-weight: bold;">École Franco-Arabe</p>' : ''}

                <div class="filters">
                    <span>Année Scolaire: ${filterAnneeScolaire || 'Toutes'}</span>
                    <span>Trimestre: ${filterTrimestre || 'Tous'}</span>
                    <span>Classe: ${classes.find(c => c.id === filterClasseId)?.name || classes.find(c => c.id === filterClasseId)?.nom || 'Toutes'}</span>
                    <span>Élève: ${filterStudentName || 'Tous'}</span>
                </div>

                <table>
                    <thead>
                        <tr>
                            <th>Classe</th>
                            <th>Élève</th>
                            <th>Matière</th>
                            <th style="text-align: center;">Trimestre</th>
                            <th style="text-align: center;">Année Scolaire</th>
                            <th style="text-align: center;">Devoir 1 (/20)</th>
                            <th style="text-align: center;">Devoir 2 (/20)</th>
                            <th style="text-align: center;">Composition/Oral (/20)</th>
                            ${isFrancoArabeSchool ? '<th style="text-align: center;">Type Matière</th>' : ''}
                        </tr>
                    </thead>
                    <tbody>
                        ${tableRows}
                    </tbody>
                </table>
            </body>
            </html>
        `;

        const printWindow = window.open('', '', 'height=600,width=800');
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
    };

    // --- handleSaveNotes adapté pour primaire et franco-arabe ---
    // --- handleSaveNotes adapté pour primaire et franco-arabe ---
const handleSaveNotes = useCallback(async (e) => {
    e.preventDefault();
    
    // Pour primaire, forcer le type d'évaluation à "Évaluation"
    const typeEvalFinal = isPrimaire ? 'Évaluation' : typeEvaluation;
    
    // Correction : ne pas vérifier typeEvaluation pour le primaire
    if (!schoolId || !matiereId || !classeId || !trimestre || !anneeScolaire || !niveau) {
        setError('Veuillez remplir tous les champs nécessaires : Année, Trimestre, Niveau, Classe et Matière.');
        return;
    }
    
    // Pour non-primaire, vérifier aussi typeEvaluation
    if (!isPrimaire && !typeEvaluation) {
        setError('Veuillez sélectionner le type d\'évaluation.');
        return;
    }
    
   
        // Vérifier que l'enseignant a accès à cette classe
        if (userRole === 'enseignant' && !canAccessClass(classeId)) {
            setError('Vous n\'êtes pas autorisé à saisir des notes pour cette classe.');
            return;
        }

        // En mode édition, on ne vérifie pas si la matière est attribuée
        // car on permet de changer de matière
        if (!editingNoteId && matieresDisponibles.length > 0) {
            const matiereEstAttribuee = matieresDisponibles.some(m => m.id === matiereId);
            if (!matiereEstAttribuee) {
                setError('Cette matière n\'est pas attribuée à la classe sélectionnée. Veuillez sélectionner une matière attribuée.');
                return;
            }
        }

        setLoading(true);
        setError(null);
        setMessage(null);
        try {
            const matiereInfo = matieresGlobales.find(m => m.id === matiereId);
            const matiereNom = matiereInfo?.name || matiereId;
            const classeNom = classes.find(c => c.id === classeId)?.name || classes.find(c => c.id === classeId)?.nom;
            
            // Récupérer la note maximale pour cette matière dans cette classe
            const liaison = liaisonsClasseMatieres.find(
                l => l.classId === classeId && l.matiereId === matiereId
            );
            const noteMax = liaison?.noteSur || (isPrimaire ? 10 : 20);
            
            // DÉTERMINER LE TYPE DE MATIÈRE
            const matiereType = liaison?.matiereType || 
                               matiereInfo?.type || 
                               determineMatiereTypeFromName(matiereNom) || 
                               'français';
            
            if (editingNoteId) {
                // --- MODE ÉDITION (1 seule note) ---
                const eleveIdToUpdate = originalEleveIdForEdit;
                const notesToSave = notesParEleve[eleveIdToUpdate] || {};
                const eleve = eleves.find(e => e.id === eleveIdToUpdate);
                const eleveNom = eleve?.name || eleve?.nom || 'N/A';
                
                let updateFields = {
                    anneeScolaire: anneeScolaire, 
                    classeId: classeId, 
                    classeNom: classeNom, 
                    eleveId: eleveIdToUpdate, 
                    eleveNom: eleveNom, 
                    matiereId: matiereId, 
                    matiereNom: matiereNom, 
                    matiereType: matiereType,
                    trimestre: trimestre, 
                    typeEvaluation: typeEvalFinal,
                    niveau: niveau,
                    updatedAt: serverTimestamp(),
                    updatedByUserId: currentUserId || null,
                };
                
                if (typeEvalFinal === 'Devoir') {
                    const note1 = cleanAndParse(notesToSave.valeurNoteDevoir1);
                    const note2 = cleanAndParse(notesToSave.valeurNoteDevoir2);
                    
                    if ((note1 !== null && (isNaN(note1) || note1 < 0 || note1 > noteMax)) || 
                        (note2 !== null && (isNaN(note2) || note2 < 0 || note2 > noteMax))) {
                        setError(`Note de Devoir invalide pour l'élève ${eleveNom}. La note maximale est ${noteMax}.`);
                        setLoading(false);
                        return;
                    }
                    
                    updateFields.valeurNoteDevoir1 = note1;
                    updateFields.valeurNoteDevoir2 = note2;
                    updateFields.valeurNote = null;
                } else {
                    const valeurNote = cleanAndParse(notesToSave.valeurNote);
                    
                    if (valeurNote !== null && (isNaN(valeurNote) || valeurNote < 0 || valeurNote > noteMax)) {
                        setError(`Note invalide pour l'élève ${eleveNom}. La note maximale est ${noteMax}.`);
                        setLoading(false);
                        return;
                    }
                    
                    updateFields.valeurNote = valeurNote;
                    updateFields.valeurNoteDevoir1 = null;
                    updateFields.valeurNoteDevoir2 = null;
                }

                // Si la matière a changé, on doit créer une nouvelle note et supprimer l'ancienne
                const existingNoteRef = doc(db, 'schools', schoolId, 'notes', editingNoteId);
                const existingNoteDoc = await getDoc(existingNoteRef);
                
                if (existingNoteDoc.exists()) {
                    const existingNoteData = existingNoteDoc.data();
                    
                    // Si la matière n'a pas changé, on met simplement à jour
                    if (existingNoteData.matiereId === matiereId) {
                        await updateDoc(existingNoteRef, updateFields);
                        setMessage('La note a été modifiée avec succès !');
                    } else {
                        // Si la matière a changé, on crée une nouvelle note et on supprime l'ancienne
                        const newNoteData = {
                            ...updateFields,
                            createdAt: serverTimestamp(),
                            createdByUserId: currentUserId || null,
                        };
                        
                        // Créer la nouvelle note
                        await addDoc(collection(db, 'schools', schoolId, 'notes'), newNoteData);
                        
                        // Supprimer l'ancienne note
                        await deleteDoc(existingNoteRef);
                        
                        setMessage('La note a été déplacée vers la nouvelle matière avec succès !');
                    }
                }

                handleCancelEdit();

            } else {
                // --- MODE CRÉATION/MISE À JOUR (Batch) ---
                const batch = writeBatch(db);
                let notesSavedCount = 0;
                let notesUpdatedCount = 0;

                for (const eleve of eleves) {
                    const notesToSave = notesParEleve[eleve.id] || {};
                    const eleveNom = eleve.name || eleve.nom;
                    const studentAuthIdValue = eleve.uid || eleve.userId || eleve.authId || null;
                    
                    let noteData = {
                        anneeScolaire: anneeScolaire,
                        classeId: classeId,
                        niveau: niveau,
                        classeNom: classeNom,
                        eleveId: eleve.id,
                        eleveNom: eleveNom,
                        matiereId: matiereId,
                        matiereNom: matiereNom,
                        matiereType: matiereType,
                        trimestre: trimestre,
                        typeEvaluation: typeEvalFinal,
                        updatedAt: serverTimestamp(),
                        studentAuthUID: studentAuthIdValue,
                        createdByUserId: currentUserId || null,
                    };
                    
                    let isValid = false;
                    
                    if (typeEvalFinal === 'Devoir') {
                        const note1 = cleanAndParse(notesToSave.valeurNoteDevoir1);
                        const note2 = cleanAndParse(notesToSave.valeurNoteDevoir2);
                        
                        if ((note1 !== null && !isNaN(note1) && note1 >= 0 && note1 <= noteMax) || 
                            (note2 !== null && !isNaN(note2) && note2 >= 0 && note2 <= noteMax)) {
                            noteData.valeurNoteDevoir1 = note1;
                            noteData.valeurNoteDevoir2 = note2;
                            noteData.valeurNote = null;
                            isValid = true;
                        } else {
                            continue;
                        }
                    } else {
                        const valeurNote = cleanAndParse(notesToSave.valeurNote);
                        if (valeurNote !== null && !isNaN(valeurNote) && valeurNote >= 0 && valeurNote <= noteMax) {
                            noteData.valeurNote = valeurNote;
                            noteData.valeurNoteDevoir1 = null;
                            noteData.valeurNoteDevoir2 = null;
                            isValid = true;
                        } else {
                            continue;
                        }
                    }

                    if (!isValid) continue;
                    
                    const q = query(
                        collection(db, 'schools', schoolId, 'notes'),
                        where('eleveId', '==', eleve.id),
                        where('matiereId', '==', matiereId),
                        where('trimestre', '==', trimestre),
                        where('anneeScolaire', '==', anneeScolaire),
                        where('typeEvaluation', '==', typeEvalFinal)
                    );
                    
                    const existingNotesSnapshot = await getDocs(q);

                    if (!existingNotesSnapshot.empty) {
                        const existingDoc = existingNotesSnapshot.docs[0];
                        const existingDocData = existingDoc.data();
                        const noteDocRef = doc(db, 'schools', schoolId, 'notes', existingDoc.id);

                        let updatePayload = {
                            updatedAt: serverTimestamp(),
                            matiereNom: matiereNom,
                            matiereType: matiereType,
                            classeNom: classeNom,
                            trimestre: trimestre,
                            typeEvaluation: typeEvalFinal,
                            niveau: niveau,
                            updatedByUserId: currentUserId || null,
                        };
                        
                        if (typeEvalFinal === 'Devoir') {
                            const newD1Value = noteData.valeurNoteDevoir1;
                            const oldD1Value = existingDocData.valeurNoteDevoir1 !== undefined ? existingDocData.valeurNoteDevoir1 : null;
                            const newD2Value = noteData.valeurNoteDevoir2;
                            const oldD2Value = existingDocData.valeurNoteDevoir2 !== undefined ? existingDocData.valeurNoteDevoir2 : null;
                            
                            updatePayload.valeurNoteDevoir1 = (newD1Value !== null) ? newD1Value : oldD1Value;
                            updatePayload.valeurNoteDevoir2 = (newD2Value !== null) ? newD2Value : oldD2Value;
                            updatePayload.valeurNote = null;
                        } else {
                            updatePayload.valeurNote = noteData.valeurNote;
                            updatePayload.valeurNoteDevoir1 = null;
                            updatePayload.valeurNoteDevoir2 = null;
                        }

                        batch.update(noteDocRef, updatePayload);
                        notesUpdatedCount++;
                    } else {
                        noteData.createdAt = serverTimestamp();
                        const newNoteRef = doc(collection(db, 'schools', schoolId, 'notes'));
                        batch.set(newNoteRef, noteData);
                        notesSavedCount++;
                    }
                }
                await batch.commit();
                setMessage(`Notes traitées avec succès : ${notesSavedCount} nouvelles notes ajoutées, ${notesUpdatedCount} notes mises à jour.`);
            }

            if (activeTab === 'saved') {
                fetchFilteredNotes();
            }
            setNotesParEleve({});
            setHasChanges(false);
        } catch (error) {
            console.error("Erreur lors de l'enregistrement/modification des notes:", error);
            setError("Erreur lors de l'enregistrement/modification des notes : " + error.message);
        }
        setLoading(false);
    }, [schoolId, matiereId, classeId, trimestre, anneeScolaire, typeEvaluation, niveau, userRole, canAccessClass, editingNoteId, matieresDisponibles, isPrimaire, matieresGlobales, classes, liaisonsClasseMatieres, originalEleveIdForEdit, notesParEleve, eleves, currentUserId, activeTab, setError, setMessage, handleCancelEdit, db, fetchFilteredNotes]);

    // --- useEffect pour recharger les notes filtrées ou agrégées ---
    useEffect(() => {
        if (activeTab === 'saved') {
            fetchFilteredNotes();
        }
    }, [activeTab, fetchFilteredNotes]);

    useEffect(() => {
        if (activeTab === 'global') {
            fetchAggregatedNotes();
        }
    }, [activeTab, fetchAggregatedNotes]);

    // --- Calculer si le bouton Enregistrer doit être désactivé ---
     // --- Calculer si le bouton Enregistrer doit être désactivé ---
const isSaveButtonDisabled = useMemo(() => {
    const basicDisabled = loading || 
                         loadingEleves || 
                         !classeId || 
                         !matiereId || 
                         (!typeEvaluation && !isPrimaire) || // Correction ici
                         (eleves.length === 0 && !editingNoteId) || 
                         (!hasChanges && !editingNoteId);
    
    // Pour les enseignants, ajouter la vérification des permissions
    if (userRole === 'enseignant') {
        if (classeId && !canAccessClass(classeId)) {
            return true;
        }
        return basicDisabled || loadingTeacherPermissions;
    }
    
    return basicDisabled;
}, [loading, loadingEleves, classeId, matiereId, typeEvaluation, isPrimaire, eleves.length, editingNoteId, hasChanges, userRole, loadingTeacherPermissions, canAccessClass]);

    // --- Rendu conditionnel si schoolId manque ---
    if (!schoolId) {
        return (
            <div className="p-6 bg-white rounded-lg shadow-md text-center text-gray-600">
                <h2 className="text-xl font-bold mb-4">Gestion des Notes</h2>
                <p>Veuillez sélectionner une école pour gérer les notes.</p>
                <p className="text-sm text-gray-500 mt-2">
                    (Le `schoolId` est manquant dans l'état de l'application.)
                </p>
            </div>
        );
    }

    return (
        <div className="p-4">
            {/* Messages Flash */}
            {error && <FlashMessage type="error" text={error} />}
            {message && <FlashMessage type="success" text={message} />}

            {/* Bannière d'information pour les enseignants */}
            {userRole === 'enseignant' && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center text-blue-800">
                        <BookUser size={18} className="mr-2" />
                        <span className="font-medium">Mode Enseignant</span>
                    </div>
                    <p className="text-sm text-blue-700 mt-1">
                        {loadingTeacherPermissions ? (
                            <span className="flex items-center">
                                <Loader2 className="animate-spin mr-2" size={14} />
                                Chargement de vos classes...
                            </span>
                        ) : teacherAssignedClasses.length > 0 ? (
                            `Vous pouvez gérer les notes pour ${teacherAssignedClasses.length} classe(s)`
                        ) : (
                            "Vous n'êtes actuellement affecté à aucune classe. Contactez l'administration."
                        )}
                    </p>
                </div>
            )}

            {/* Onglets */}
            <div className="mb-6 border-b border-gray-200">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                    <button
                        onClick={() => setActiveTab('entry')}
                        className={`${
                            activeTab === 'entry'
                                ? 'border-green-600 text-green-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                    >
                        <BookText className="mr-2" /> Saisie des Notes
                    </button>
      
                    <button
                        onClick={() => setActiveTab('saved')}
                        className={`${
                            activeTab === 'saved'
                                ? 'border-green-600 text-green-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                    >
                        <ListTodo className="mr-2" /> Notes Enregistrées
                    </button>
                    
                    <button
                        onClick={() => setActiveTab('global')}
                        className={`${
                            activeTab === 'global'
                            ? 'border-green-600 text-green-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                    >
                        <ListTodo className="mr-2" /> Synthèse des Notes
                    </button>
                </nav>
            </div>

            {activeTab === 'entry' && (
                <div className="tab-content">
                    <h2 className="text-xl font-semibold mb-4 flex items-center">
                        <BookText className="mr-2" /> {editingNoteId ? 'Modifier la Note' : isMassEditMode ? 'Édition en Masse' : 'Saisie des Notes par Classe'}
                        {isPrimaire && (
                            <span className="ml-2 text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                Mode Primaire/Préscolaire
                            </span>
                        )}
                        {isFrancoArabeSchool && (
                            <span className="ml-2 text-sm bg-green-100 text-green-800 px-2 py-1 rounded-full flex items-center">
                                <Globe size={12} className="mr-1" /> Franco-Arabe
                            </span>
                        )}
                    </h2>

                    <form onSubmit={isMassEditMode ? (e) => { e.preventDefault(); handleApplyMassEdit(); } : handleSaveNotes} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {/* Année scolaire */}
                            <select
                                className="border p-2 rounded"
                                value={anneeScolaire}
                                onChange={(e) => setAnneeScolaire(e.target.value)}
                                required
                                disabled={editingNoteId || isMassEditMode}
                            >
                                <option value="">Sélectionner l'Année scolaire</option>
                                {generateAcademicYears().map((year) => (
                                    <option key={year} value={year}>
                                        {year}
                                    </option>
                                ))}
                            </select>

                            {/* Trimestre */}
                            <select
                                className="border p-2 rounded"
                                value={trimestre}
                                onChange={(e) => setTrimestre(e.target.value)}
                                required
                                disabled={editingNoteId || isMassEditMode}
                            >
                                <option value="">Sélectionner le Trimestre</option>
                                <option value="1er Trimestre">1er Trimestre</option>
                                <option value="2ème Trimestre">2ème Trimestre</option>
                                <option value="3ème Trimestre">3ème Trimestre</option>
                            </select>

                            {/* Niveau - Masqué pour les enseignants */}
                            {userRole !== 'enseignant' && (
                                <select
                                    className="border p-2 rounded"
                                    value={niveau}
                                    onChange={(e) => {
                                        setNiveau(e.target.value);
                                        setClasseId('');
                                        setMatiereId('');
                                        setMatieresAttribuees([]);
                                        setMatieresDisponibles([]);
                                    }}
                                    required
                                    disabled={editingNoteId || isMassEditMode}
                                >
                                    <option value="">Sélectionner le Niveau</option>
                                    {availableNiveaux.map((niv) => (
                                        <option key={niv} value={niv}>{niv}</option>
                                    ))}
                                </select>
                            )}

                            {/* Classe */}
                            <select
                                className="border p-2 rounded"
                                value={classeId}
                                onChange={(e) => {
                                    const newClassId = e.target.value;
                                    setClasseId(newClassId);
                                    setMatiereId('');
                                    setClasseId(e.target.value);
                                    setMatiereId('');
                                    const selectedClass = classes.find(c => c.id === newClassId);
                                    if (selectedClass && selectedClass.niveau) {
                                    setNiveau(selectedClass.niveau);
                                }
                                }}
                                required
                                disabled={editingNoteId || (userRole !== 'enseignant' && !niveau) || isMassEditMode || loadingTeacherPermissions}
                            >
                                <option value="">Sélectionner la Classe</option>
                                {userRole === 'enseignant' ? (
                                    loadingTeacherPermissions ? (
                                        <option value="" disabled>Chargement de vos classes...</option>
                                    ) : filteredClasses.length === 0 ? (
                                        <option value="" disabled>
                                            Aucune classe attribuée. Contactez l'administration.
                                        </option>
                                    ) : (
                                        filteredClasses.map((classe) => (
                                            <option key={classe.id} value={classe.id}>
                                                {classe.name || classe.nom}
                                            </option>
                                        ))
                                    )
                                ) : (
                                    filteredClasses.map((classe) => (
                                        <option key={classe.id} value={classe.id}>
                                            {classe.name || classe.nom}
                                        </option>
                                    ))
                                )}
                            </select>
                           
                            {/* Matière */}
                            <select
                                className="border p-2 rounded"
                                value={isMassEditMode ? massEditMatiereId : matiereId}
                                onChange={(e) => isMassEditMode ? setMassEditMatiereId(e.target.value) : setMatiereId(e.target.value)}
                                required
                                disabled={!classeId || loadingMatieres}
                            >
                                <option value="">Sélectionner une Matière</option>
                                {loadingMatieres ? (
                                    <option value="" disabled>Chargement des matières...</option>
                                ) : matieresDisponibles.length === 0 ? (
                                    <option value="" disabled>
                                        {classeId ? 
                                            'Aucune matière attribuée à cette classe' : 
                                            'Sélectionnez d\'abord une classe'}
                                    </option>
                                ) : (
                                    matieresDisponibles.map((matiere) => {
                                        const coefficientInfo = matiere.coefficient ? `(Coeff: ${matiere.coefficient})` : '';
                                        const noteSurInfo = matiere.noteSur ? `sur ${matiere.noteSur}` : '';
                                        const niveauInfo = matiere.niveau ? `[${matiere.niveau}]` : '';
                                        const matiereType = matiere.matiereType || matiere.type || 'français';
                                        const typeLetter = matiereType === 'arabe' ? 'A' : matiereType === 'commun' ? 'C' : 'F';
                                        
                                        return (
                                            <option key={matiere.id} value={matiere.id}>
                                                {matiere.name} [${typeLetter}] ${niveauInfo} ${coefficientInfo} ${noteSurInfo}
                                            </option>
                                        );
                                    })
                                )}
                            </select>
                         
                            {/* Type d'évaluation */}
                            {!isPrimaire ? (
                                <select
                                    className="border p-2 rounded"
                                    value={typeEvaluation}
                                    onChange={(e) => setTypeEvaluation(e.target.value)}
                                    required
                                    disabled={editingNoteId || isMassEditMode}
                                >
                                    <option value="">Sélectionner le Type d'Évaluation</option>
                                    {typeEvaluationOptions.map((type) => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <div className="border p-2 rounded bg-blue-50">
                                    <p className="text-sm font-medium text-blue-800">
                                        Type d'Évaluation: <span className="font-bold">Évaluation (Primaire)</span>
                                    </p>
                                    <p className="text-xs text-blue-600 mt-1">
                                        Une seule note par matière et par trimestre
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* NOUVEAU : Filtre pour école franco-arabe */}
                        {isFrancoArabeSchool && classeId && matieresAttribuees.length > 0 && (
                            <div className="p-3 rounded-lg border bg-green-50 border-green-200">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center text-green-800">
                                        <Languages size={16} className="mr-2" />
                                        <span className="font-medium">Filtre Matières Franco-Arabe</span>
                                    </div>
                                    <select
                                        value={selectedMatiereTypeFilter}
                                        onChange={(e) => setSelectedMatiereTypeFilter(e.target.value)}
                                        className="border p-1 rounded text-sm"
                                    >
                                        <option value="tous">Toutes les matières</option>
                                        <option value="français">Matières Françaises</option>
                                        <option value="arabe">Matières Arabes</option>
                                    </select>
                                </div>
                                <p className="text-sm text-green-600 mt-1">
                                    {selectedMatiereTypeFilter === 'tous' ? 'Affichage de toutes les matières' :
                                     selectedMatiereTypeFilter === 'français' ? 'Matières françaises + communes' :
                                     'Matières arabes + communes'}
                                </p>
                            </div>
                        )}

                        {/* Information sur les matières attribuées */}
                        {classeId && matieresAttribuees.length > 0 && (
                            <div className={`p-3 rounded-lg border ${isPrimaire ? 'bg-blue-50 border-blue-200' : 'bg-blue-50 border-blue-200'}`}>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center text-blue-800 mb-1">
                                        <Filter size={16} className="mr-2" />
                                        <span className="font-medium">
                                            {matieresAttribuees.length} matière(s) attribuée(s) à cette classe
                                        </span>
                                        {isPrimaire && (
                                            <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                                Notation sur {matieresAttribuees[0]?.noteSur || 10}
                                            </span>
                                        )}
                                    </div>
                                    {!isMassEditMode && !editingNoteId && matiereId && (
                                        <button
                                            type="button"
                                            onClick={() => handleStartMassEdit(matiereId)}
                                            className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 flex items-center"
                                        >
                                            <Edit3 size={14} className="mr-1" />
                                            Édition en masse
                                        </button>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {matieresAttribuees.slice(0, 8).map(matiere => {
                                        const matiereType = matiere.matiereType || matiere.type || 'français';
                                        const typeColor = matiereType === 'arabe' ? 'bg-green-100 text-green-800 border border-green-300' : 
                                                         matiereType === 'commun' ? 'bg-purple-100 text-purple-800 border border-purple-300' : 
                                                         'bg-blue-100 text-blue-800 border border-blue-300';
                                        const isSelected = matiereId === matiere.id || massEditMatiereId === matiere.id;
                                        
                                        return (
                                            <span 
                                                key={matiere.id} 
                                                className={`px-2 py-1 text-xs rounded-full ${isSelected ? typeColor : 'bg-gray-100 text-gray-700'}`}
                                            >
                                                {matiere.name}
                                                <span className={`ml-1 text-xs px-1 rounded ${typeColor}`}>
                                                    {matiereType === 'arabe' ? 'A' : matiereType === 'commun' ? 'C' : 'F'}
                                                </span>
                                                {matiere.coefficient && matiere.coefficient !== 1 && (
                                                    <span className="ml-1 text-xs">(×{matiere.coefficient})</span>
                                                )}
                                            </span>
                                        );
                                    })}
                                    {matieresAttribuees.length > 8 && (
                                        <span className="px-2 py-1 text-xs text-gray-500">
                                            + {matieresAttribuees.length - 8} autres...
                                        </span>
                                    )}
                                </div>
                                {editingNoteId && (
                                    <p className="text-sm text-blue-600 mt-2">
                                        ⓘ En mode édition, vous pouvez changer la matière de cette note.
                                    </p>
                                )}
                                {isPrimaire && (
                                    <p className="text-sm text-blue-600 mt-2">
                                        ⓘ Pour le niveau primaire/préscolaire : saisissez une seule note par matière et par trimestre.
                                        Le bulletin affichera automatiquement les 3 trimestres.
                                    </p>
                                )}
                                {isFrancoArabeSchool && (
                                    <div className="mt-2 flex items-center text-sm text-green-700">
                                        <BookOpen size={14} className="mr-1" />
                                        <span>Les notes sont automatiquement classées par type de matière pour les bulletins séparés.</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Affichage des élèves pour la saisie/édition */}
                        {((classeId && (matiereId || massEditMatiereId) && (typeEvaluation || isPrimaire)) || editingNoteId) && (
                            <div className="overflow-auto max-h-[400px] border p-3 rounded">
                                {loadingEleves && !editingNoteId ? (
                                    <div className="flex justify-center items-center h-48">
                                        <Loader2 className="animate-spin mr-2" size={24} /> Chargement des élèves...
                                    </div>
                                ) : (eleves.length === 0 && !editingNoteId) ? (
                                    <p className="text-center text-gray-500 py-8">Aucun élève trouvé pour cette classe ou pas encore d'inscriptions.</p>
                                ) : (
                                    <div>
                                        {isMassEditMode && (
                                            <div className="bg-purple-50 p-3 mb-4 rounded-lg border border-purple-200">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center text-purple-800">
                                                        <Edit3 size={16} className="mr-2" />
                                                        <span className="font-medium">Mode édition en masse</span>
                                                    </div>
                                                    <div className="flex items-center">
                                                        {loadingExistingNotes ? (
                                                            <span className="text-sm text-purple-600 mr-3">
                                                                <Loader2 className="animate-spin inline mr-1" size={14} />
                                                                Chargement des notes existantes...
                                                            </span>
                                                        ) : (
                                                            <>
                                                                <span className="text-sm text-purple-700 mr-3">
                                                                    {selectedNotesForMassEdit.length} élève(s) sélectionné(s)
                                                                </span>
                                                                <button
                                                                    type="button"
                                                                    onClick={handleSelectAllMassEdit}
                                                                    className="text-sm text-purple-600 hover:text-purple-800"
                                                                >
                                                                    {selectAllMassEdit ? 'Tout désélectionner' : 'Tout sélectionner'}
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                <p className="text-sm text-purple-600 mt-1">
                                                    Les champs sont pré-remplis avec les notes existantes. Cochez les élèves à modifier.
                                                </p>
                                            </div>
                                        )}
                                        
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="bg-gray-100">
                                                    {isMassEditMode && (
                                                        <th className="text-left p-2 w-10">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectAllMassEdit}
                                                                onChange={handleSelectAllMassEdit}
                                                                className="rounded"
                                                                disabled={loadingExistingNotes}
                                                            />
                                                        </th>
                                                    )}
                                                    <th className="text-left p-2">Élève</th>
                                                    {(!isPrimaire && typeEvaluation === 'Devoir') ? 
                                                    (
                                                        <>
                                                            <th className="text-left p-2">Devoir 1 /{matieresAttribuees.find(m => m.id === (isMassEditMode ? massEditMatiereId : matiereId))?.noteSur || 20}</th>
                                                            <th className="text-left p-2">Devoir 2 /{matieresAttribuees.find(m => m.id === (isMassEditMode ? massEditMatiereId : matiereId))?.noteSur || 20}</th>
                                                        </>
                                                    ) : (
                                                        <th className="text-left p-2">
                                                            Note /{matieresAttribuees.find(m => m.id === (isMassEditMode ? massEditMatiereId : matiereId))?.noteSur || (isPrimaire ? 10 : 20)}
                                                            {isPrimaire && <span className="text-xs text-gray-500 block">(Trimestre {trimestre})</span>}
                                                        </th>
                                                    )}
                                                    {isMassEditMode && (
                                                        <th className="text-left p-2 text-xs text-gray-500">Note existante</th>
                                                    )}
                                                </tr>
                                            </thead>
                                           
                                            <tbody>
                                                {(editingNoteId ? eleves.filter(e => e.id === originalEleveIdForEdit) : eleves).map((eleve) => {
                                                    const existingNote = existingNotesForMassEdit[eleve.id];
                                                    const hasExistingNote = existingNote && 
                                                        ((typeEvaluation === 'Devoir' && (existingNote.valeurNoteDevoir1 !== null || existingNote.valeurNoteDevoir2 !== null)) ||
                                                         (typeEvaluation !== 'Devoir' && existingNote.valeurNote !== null));
                                                    
                                                    // Déterminer la note maximale
                                                    const matiereNoteMax = matieresAttribuees.find(m => m.id === (isMassEditMode ? massEditMatiereId : matiereId))?.noteSur || (isPrimaire ? 10 : 20);
                                                    
                                                    return (
                                                        <tr key={eleve.id} className={`border-t ${hasExistingNote ? 'bg-yellow-50' : 'hover:bg-gray-50'}`}>
                                                            {isMassEditMode && (
                                                                <td className="p-2">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedNotesForMassEdit.includes(eleve.id)}
                                                                        onChange={() => handleToggleNoteForMassEdit(eleve.id)}
                                                                        className="rounded"
                                                                        disabled={loadingExistingNotes}
                                                                    />
                                                                </td>
                                                            )}
                                                            <td className="p-2">
                                                                <div className="flex items-center">
                                                                    {eleve.name || eleve.nom}
                                                                    {hasExistingNote && (
                                                                        <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-1 py-0.5 rounded">
                                                                            Note existante
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            
                                                            {(!isPrimaire && typeEvaluation === 'Devoir') ? (
                                                                <>
                                                                    <td className="p-2">
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            max={matiereNoteMax}
                                                                            step="0.1"
                                                                            value={isMassEditMode ? 
                                                                                (massEditValues[eleve.id]?.valeurNoteDevoir1 || '') : 
                                                                                (notesParEleve[eleve.id]?.valeurNoteDevoir1 || '')
                                                                            }
                                                                            onChange={(e) => {
                                                                                if (isMassEditMode) {
                                                                                    handleMassEditChange(eleve.id, e.target.value, 'valeurNoteDevoir1');
                                                                                } else {
                                                                                    handleNoteChange(eleve.id, e.target.value, 'valeurNoteDevoir1');
                                                                                }
                                                                            }}
                                                                            className={`border px-2 py-1 w-24 rounded ${hasExistingNote ? 'border-yellow-300 bg-yellow-50' : ''}`}
                                                                            disabled={isMassEditMode && !selectedNotesForMassEdit.includes(eleve.id)}
                                                                            placeholder={`max ${matiereNoteMax}`}
                                                                        />
                                                                    </td>
                                             
                                                                    <td className="p-2">
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            max={matiereNoteMax}
                                                                            step="0.1"
                                                                            value={isMassEditMode ? 
                                                                                (massEditValues[eleve.id]?.valeurNoteDevoir2 || '') : 
                                                                                (notesParEleve[eleve.id]?.valeurNoteDevoir2 || '')
                                                                            }
                                                                            onChange={(e) => {
                                                                                if (isMassEditMode) {
                                                                                    handleMassEditChange(eleve.id, e.target.value, 'valeurNoteDevoir2');
                                                                                } else {
                                                                                    handleNoteChange(eleve.id, e.target.value, 'valeurNoteDevoir2');
                                                                                }
                                                                            }}
                                                                            className={`border px-2 py-1 w-24 rounded ${hasExistingNote ? 'border-yellow-300 bg-yellow-50' : ''}`}
                                                                            disabled={isMassEditMode && !selectedNotesForMassEdit.includes(eleve.id)}
                                                                            placeholder={`max ${matiereNoteMax}`}
                                                                        />
                                                                    </td>
                                                                </>
                                                            ) : (
                                                                <td className="p-2">
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        max={matiereNoteMax}
                                                                        step="0.1"
                                                                        value={isMassEditMode ? 
                                                                            (massEditValues[eleve.id]?.valeurNote || '') : 
                                                                            (notesParEleve[eleve.id]?.valeurNote || '')
                                                                        }
                                                                        onChange={(e) => {
                                                                            if (isMassEditMode) {
                                                                                handleMassEditChange(eleve.id, e.target.value, 'valeurNote');
                                                                            } else {
                                                                                handleNoteChange(eleve.id, e.target.value, 'valeurNote');
                                                                            }
                                                                        }}
                                                                        className={`border px-2 py-1 w-24 rounded ${hasExistingNote ? 'border-yellow-300 bg-yellow-50' : ''}`}
                                                                        disabled={isMassEditMode && !selectedNotesForMassEdit.includes(eleve.id)}
                                                                        placeholder={`max ${matiereNoteMax}`}
                                                                    />
                                                                </td>
                                                            )}
                                                            
                                                            {isMassEditMode && existingNote && (
                                                                <td className="p-2 text-xs text-gray-500">
                                                                    {typeEvaluation === 'Devoir' ? (
                                                                        <div>
                                                                            {existingNote.valeurNoteDevoir1 !== null && existingNote.valeurNoteDevoir1 !== undefined ? 
                                                                                `D1: ${existingNote.valeurNoteDevoir1}` : 'D1: -'}
                                                                            {existingNote.valeurNoteDevoir2 !== null && existingNote.valeurNoteDevoir2 !== undefined ? 
                                                                                `, D2: ${existingNote.valeurNoteDevoir2}` : ', D2: -'}
                                                                        </div>
                                                                    ) : (
                                                                        <div>
                                                                            {existingNote.valeurNote !== null && existingNote.valeurNote !== undefined ? 
                                                                                `Note: ${existingNote.valeurNote}` : 'Note: -'}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            )}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="text-right flex gap-2 justify-end">
                            {isMassEditMode ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={handleCancelMassEdit}
                                        className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 flex items-center"
                                    >
                                        <X size={16} className="mr-1" />
                                        Annuler
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={massEditLoading || selectedNotesForMassEdit.length === 0 || loadingExistingNotes}
                                        className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                    >
                                        {massEditLoading ? (
                                            <span className="flex items-center justify-center">
                                                <Loader2 className="animate-spin mr-2" /> Application...
                                            </span>
                                        ) : (
                                            <>
                                                <Check size={16} className="mr-1" />
                                                Appliquer ({selectedNotesForMassEdit.length})
                                            </>
                                        )}
                                    </button>
                                </>
                            ) : (
                                <>
                                    {(editingNoteId || (classeId && matiereId && (typeEvaluation || isPrimaire))) && (
                                        <button
                                            type="button"
                                            onClick={handleCancelEdit}
                                            className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
                                        >
                                            {editingNoteId ? 'Annuler l\'édition' : 'Réinitialiser'}
                                        </button>
                                    )}
                                    <button
                                        type="submit"
                                        disabled={isSaveButtonDisabled}
                                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {loading
                                        ? (
                                            <span className="flex items-center justify-center">
                                                <Loader2 className="animate-spin mr-2" /> {editingNoteId ? 'Modification...' : 'Enregistrement...'}
                                            </span>
                                        ) : (
                                            editingNoteId ? 'Modifier la Note' : 'Enregistrer les Notes'
                                        )}
                                    </button>
                                </>
                            )}
                        </div>
                    </form>
                </div>
            )}

            {activeTab === 'saved' && (
                <div className="tab-content">
                    <h2 className="text-xl font-semibold mb-4 flex items-center">
                        <ListTodo className="mr-2" /> Notes Enregistrées
                        {isFrancoArabeSchool && (
                            <span className="ml-2 text-sm bg-green-100 text-green-800 px-2 py-1 rounded-full flex items-center">
                                <Globe size={12} className="mr-1" /> Franco-Arabe
                            </span>
                        )}
                    </h2>

                    {/* Filtres */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                        <select
                            className="border p-2 rounded"
                            value={filterAnneeScolaire}
                            onChange={(e) => setFilterAnneeScolaire(e.target.value)}
                        >
                            <option value="">Toutes les années</option>
                            {generateAcademicYears().map((year) => (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            ))}
                        </select>

                        <select
                            className="border p-2 rounded"
                            value={filterTrimestre}
                            onChange={(e) => setFilterTrimestre(e.target.value)}
                        >
                            <option value="">Tous les trimestres</option>
                            <option value="1er Trimestre">1er Trimestre</option>
                            <option value="2ème Trimestre">2ème Trimestre</option>
                            <option value="3ème Trimestre">3ème Trimestre</option>
                        </select>
                        
                        <select
                            className="border p-2 rounded"
                            value={filterTypeEvaluation}
                            onChange={(e) => setFilterTypeEvaluation(e.target.value)}
                        >
                            <option value="">Tous les types d'éval.</option>
                            {typeEvaluationOptions.map((type) => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </select>

                        <select
                            className="border p-2 rounded"
                            value={filterClasseId}
                            onChange={(e) => setFilterClasseId(e.target.value)}
                            disabled={userRole === 'enseignant' && teacherAssignedClasses.length === 0}
                        >
                            <option value="">Toutes les classes</option>
                            {userRole === 'enseignant' ? (
                                teacherAssignedClasses.length === 0 ? (
                                    <option value="" disabled>Aucune classe attribuée</option>
                                ) : (
                                    classes
                                        .filter(classe => teacherAssignedClasses.includes(classe.id))
                                        .map((classe) => (
                                            <option key={classe.id} value={classe.id}>
                                                {classe.name || classe.nom}
                                            </option>
                                        ))
                                )
                            ) : (
                                classes.map((classe) => (
                                    <option key={classe.id} value={classe.id}>
                                        {classe.name || classe.nom}
                                    </option>
                                ))
                            )}
                        </select>

                        <input
                            type="text"
                            placeholder="Filtrer par nom d'élève"
                            className="border p-2 rounded"
                            value={filterStudentName}
                            onChange={(e) => setFilterStudentName(e.target.value)}
                        />
                    </div>

                    {/* Affichage des notes filtrées */}
                    <div className="overflow-auto max-h-[600px] border p-3 rounded">
                        {loadingFilteredNotes ? (
                            <div className="flex justify-center items-center h-48">
                                <Loader2 className="animate-spin mr-2" size={24} /> Chargement des notes...
                            </div>
                        ) : filteredNotes.length === 0 ? (
                            <p className="text-center text-gray-500 py-8">
                                {userRole === 'enseignant' && teacherAssignedClasses.length === 0 
                                    ? "Vous n'êtes pas affecté à des classes. Contactez l'administration."
                                    : "Aucune note trouvée correspondant aux critères de filtre."
                                }
                            </p>
                        ) : (
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <th className="text-left p-2">Classe</th>
                                        <th className="text-left p-2">Élève</th>
                                        <th className="text-left p-2">Matière</th>
                                        {isFrancoArabeSchool && <th className="text-left p-2">Type</th>}
                                        <th className="text-left p-2">Trimestre</th>
                                        <th className="text-left p-2">Type d'éval.</th>
                                        <th className="text-left p-2">Année Scolaire</th>
                                        <th className="text-center p-2">Note Comp./Oral (/20)</th>
                                        <th className="text-center p-2">Devoir 1 (/20)</th> 
                                        <th className="text-center p-2">Devoir 2 (/20)</th> 
                                        <th className="text-center p-2">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredNotes.map((note) => {
                                        // Déterminer si c'est primaire
                                        const classeNote = classes.find(c => c.id === note.classeId);
                                        const isPrimaireNote = classeNote?.niveau?.toLowerCase().includes('élémentaire') || 
                                                              classeNote?.niveau?.toLowerCase().includes('préscolaire') ||
                                                              classeNote?.niveau?.toLowerCase().includes('prescolaire');
                                        
                                        const matiereType = note.matiereType || determineMatiereTypeFromName(note.matiereNom) || 'français';
                                        const typeColor = matiereType === 'arabe' ? 'bg-green-100 text-green-800' : 
                                                         matiereType === 'commun' ? 'bg-purple-100 text-purple-800' : 
                                                         'bg-blue-100 text-blue-800';
                                        
                                        return (
                                            <tr key={note.id} className="border-t">
                                                <td className="p-2">{note.classeNom || 'N/A'}</td>
                                                <td className="p-2">{note.eleveNom || 'N/A'}</td>
                                                <td className="p-2">{note.matiereNom || 'N/A'}</td>
                                                {isFrancoArabeSchool && (
                                                    <td className="p-2">
                                                        <span className={`px-2 py-1 text-xs rounded-full ${typeColor}`}>
                                                            {matiereType === 'arabe' ? 'Arabe' : 
                                                             matiereType === 'commun' ? 'Commun' : 'Français'}
                                                        </span>
                                                    </td>
                                                )}
                                                <td className="p-2">{note.trimestre || 'N/A'}</td>
                                                <td className="p-2 font-semibold">
                                                    {note.typeEvaluation || 'Devoir'}
                                                    {isPrimaireNote && note.typeEvaluation === 'Évaluation' && (
                                                        <span className="ml-1 text-xs bg-blue-100 text-blue-800 px-1 py-0.5 rounded">Primaire</span>
                                                    )}
                                                </td>
                                                <td className="p-2">{note.anneeScolaire || 'N/A'}</td>
                                                
                                                <td className="p-2 font-medium text-center">
                                                    {note.typeEvaluation !== 'Devoir' && (note.valeurNote !== undefined && note.valeurNote !== null ? note.valeurNote : 'N/A')}
                                                    {note.typeEvaluation === 'Devoir' && 'N/A'}
                                                </td>

                                                <td className="p-2 font-medium text-center">
                                                    {note.typeEvaluation === 'Devoir' ? (note.valeurNoteDevoir1 !== undefined && note.valeurNoteDevoir1 !== null ? note.valeurNoteDevoir1 : 'N/A') : 'N/A'}
                                                </td>
                                                
                                                <td className="p-2 font-medium text-center">
                                                    {note.typeEvaluation === 'Devoir' ? (note.valeurNoteDevoir2 !== undefined && note.valeurNoteDevoir2 !== null ? note.valeurNoteDevoir2 : 'N/A') : 'N/A'}
                                                </td>
                                                
                                                <td className="p-2 text-center flex justify-center space-x-2">
                                                    {(userRole === 'admin' || userRole === 'directeurPedagogique' || 
                                                      (userRole === 'enseignant' && canAccessClass(note.classeId))) && (
                                                        <>
                                                            <button
                                                                onClick={() => handleEditNote(note)}
                                                                className="text-blue-600 hover:text-blue-800"
                                                                title="Modifier la note"
                                                            >
                                                                <Edit size={16} />
                                                            </button>
                         
                                                            <button
                                                                onClick={() => handleDeleteNote(note.id, note.eleveNom, note.classeId)}
                                                                className="text-red-600 hover:text-red-800"
                                                                title="Supprimer la note"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}
            
            {/* Vue Globale des Notes Agrégées */}
            {activeTab === 'global' && (
                <div className="tab-content">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold flex items-center">
                            <ListTodo className="mr-2" /> Synthèse des Notes Globales
                            {isFrancoArabeSchool && (
                                <span className="ml-2 text-sm bg-green-100 text-green-800 px-2 py-1 rounded-full flex items-center">
                                    <Globe size={12} className="mr-1" /> Franco-Arabe
                                </span>
                            )}
                        </h2>
                        {aggregatedNotes.length > 0 && (
                            <button
                                onClick={handlePrintGlobalNotes}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center"
                                disabled={loadingAggregatedNotes}
                            >
                                <Printer size={20} className="mr-2" /> Imprimer
                            </button>
                        )}
                    </div>

                    {/* Filtres de la vue globale */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                        <select
                            className="border p-2 rounded"
                            value={filterAnneeScolaire}
                            onChange={(e) => setFilterAnneeScolaire(e.target.value)}
                        >
                            <option value="">Toutes les années</option>
                            {generateAcademicYears().map((year) => (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            ))}
                        </select>

                        <select
                            className="border p-2 rounded"
                            value={filterTrimestre}
                            onChange={(e) => setFilterTrimestre(e.target.value)}
                        >
                            <option value="">Tous les trimestres</option>
                            <option value="1er Trimestre">1er Trimestre</option>
                            <option value="2ème Trimestre">2ème Trimestre</option>
                            <option value="3ème Trimestre">3ème Trimestre</option>
                        </select>
                        
                        <div className="col-span-1"></div>

                        <select
                            className="border p-2 rounded"
                            value={filterClasseId}
                            onChange={(e) => setFilterClasseId(e.target.value)}
                            disabled={userRole === 'enseignant' && teacherAssignedClasses.length === 0}
                        >
                            <option value="">Toutes les classes</option>
                            {userRole === 'enseignant' ? (
                                teacherAssignedClasses.length === 0 ? (
                                    <option value="" disabled>Aucune classe attribuée</option>
                                ) : (
                                    classes
                                        .filter(classe => teacherAssignedClasses.includes(classe.id))
                                        .map((classe) => (
                                            <option key={classe.id} value={classe.id}>
                                                {classe.name || classe.nom}
                                            </option>
                                        ))
                                )
                            ) : (
                                classes.map((classe) => (
                                    <option key={classe.id} value={classe.id}>
                                        {classe.name || classe.nom}
                                    </option>
                                ))
                            )}
                        </select>

                        <input
                            type="text"
                            placeholder="Filtrer par nom d'élève"
                            className="border p-2 rounded"
                            value={filterStudentName}
                            onChange={(e) => setFilterStudentName(e.target.value)}
                        />
                    </div>

                    {/* Affichage des notes agrégées */}
                    <div className="overflow-auto max-h-[600px] border p-3 rounded">
                        {loadingAggregatedNotes ? (
                            <div className="flex justify-center items-center h-48">
                                <Loader2 className="animate-spin mr-2" size={24} /> Chargement de la synthèse des notes...
                            </div>
                        ) : aggregatedNotes.length === 0 ? (
                            <p className="text-center text-gray-500 py-8">
                                {userRole === 'enseignant' && teacherAssignedClasses.length === 0 
                                    ? "Vous n'êtes pas affecté à des classes. Contactez l'administration."
                                    : "Aucune note globale trouvée correspondant aux critères de filtre."
                                }
                            </p>
                        ) : (
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <th className="text-left p-2">Classe</th>
                                        <th className="text-left p-2">Élève</th>
                                        <th className="text-left p-2">Matière</th>
                                        <th className="text-left p-2">Trimestre</th>
                                        <th className="text-left p-2">Année Scolaire</th>
                                        <th className="text-center p-2">Devoir 1 (/20)</th> 
                                        <th className="text-center p-2">Devoir 2 (/20)</th> 
                                        <th className="text-center p-2">Composition/Oral (/20)</th> 
                                        {isFrancoArabeSchool && <th className="text-center p-2">Type Matière</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {aggregatedNotes.map((note, index) => {
                                        const typeColor = note.matiereType === 'arabe' ? 'bg-green-100 text-green-800' : 
                                                         note.matiereType === 'commun' ? 'bg-purple-100 text-purple-800' : 
                                                         'bg-blue-100 text-blue-800';
                                        
                                        return (
                                            <tr key={index} className="border-t">
                                                <td className="p-2">{note.classeNom || 'N/A'}</td>
                                                <td className="p-2">{note.eleveNom || 'N/A'}</td>
                                                <td className="p-2">{note.matiereNom || 'N/A'}</td>
                                                <td className="p-2">{note.trimestre || 'N/A'}</td>
                                                <td className="p-2">{note.anneeScolaire || 'N/A'}</td>
                                                <td className="p-2 font-medium text-center">
                                                    {note.valeurNoteDevoir1 !== null ? note.valeurNoteDevoir1 : '-'}
                                                </td>
                                                <td className="p-2 font-medium text-center">
                                                    {note.valeurNoteDevoir2 !== null ? note.valeurNoteDevoir2 : '-'}
                                                </td>
                                                <td className="p-2 font-medium text-center">
                                                    {note.valeurNoteCompoOral !== null ? note.valeurNoteCompoOral : '-'}
                                                </td>
                                                {isFrancoArabeSchool && (
                                                    <td className="p-2 text-center">
                                                        <span className={`px-2 py-1 text-xs rounded-full ${typeColor}`}>
                                                            {note.matiereType === 'arabe' ? 'Arabe' : 
                                                             note.matiereType === 'commun' ? 'Commun' : 'Français'}
                                                        </span>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotesManagement;